// Truth API endpoints

import { get, post, del } from './client';
import type {
  ApiResponse,
  Truth,
  TruthCreateRequest,
  ReplyCreateRequest,
  Reply,
  InteractionResponse,
  SearchResponse,
} from './types';

export type SortOption = 'recent' | 'trending' | 'top';
export type ReplySortOption = 'recent' | 'top';
export type SearchType = 'all' | 'truths' | 'replies';

export async function getTruths(
  sort: SortOption = 'recent',
  limit = 25,
  offset = 0
): Promise<ApiResponse<Truth[]>> {
  return get(`/truths?sort=${sort}&limit=${limit}&offset=${offset}`);
}

export async function getTimeline(
  limit = 25,
  offset = 0
): Promise<ApiResponse<Truth[]>> {
  return get(`/timeline?limit=${limit}&offset=${offset}`);
}

export async function getTruth(truthId: string): Promise<ApiResponse<Truth>> {
  return get(`/truths/${truthId}`);
}

export async function createTruth(
  request: TruthCreateRequest
): Promise<ApiResponse<Truth>> {
  return post('/truths', request);
}

export async function deleteTruth(truthId: string): Promise<ApiResponse> {
  return del(`/truths/${truthId}`);
}

export async function likeTruth(truthId: string): Promise<InteractionResponse> {
  return post(`/truths/${truthId}/like`);
}

export async function unlikeTruth(truthId: string): Promise<InteractionResponse> {
  return del(`/truths/${truthId}/like`);
}

export async function retruth(truthId: string): Promise<InteractionResponse> {
  return post(`/truths/${truthId}/retruth`);
}

// Replies
export async function getReplies(
  truthId: string,
  sort: ReplySortOption = 'recent',
  limit = 25,
  offset = 0
): Promise<ApiResponse<Reply[]>> {
  return get(`/truths/${truthId}/replies?sort=${sort}&limit=${limit}&offset=${offset}`);
}

export async function createReply(
  truthId: string,
  request: ReplyCreateRequest
): Promise<{ success: boolean; data: Reply; author?: { name: string }; already_following?: boolean }> {
  return post(`/truths/${truthId}/replies`, request);
}

export async function likeReply(replyId: string): Promise<InteractionResponse> {
  return post(`/replies/${replyId}/like`);
}

export async function unlikeReply(replyId: string): Promise<InteractionResponse> {
  return del(`/replies/${replyId}/like`);
}

// Search
export async function search(
  query: string,
  type: SearchType = 'all',
  limit = 20
): Promise<SearchResponse> {
  return get(`/search?q=${encodeURIComponent(query)}&type=${type}&limit=${limit}`);
}
